import { useHead } from '@vueuse/head'
import type { HeadObjectPlain } from '@vueuse/head'
import type { SchemaType, ISchemaOptions } from '&/types/schema'
import { createWebSiteSchema, createOrganizationSchema, createProductSchema, toJsonLd } from './schema'

export interface SeoOptions {
  title?: string
  description?: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogUrl?: string
  ogType?: string
  schema?: SchemaType | SchemaType[]
}

export function useSeo(options: SeoOptions = {}) {
  const defaultTitle = 'Flashbase - 定制您的专属产品'
  const defaultDescription = 'Flashbase提供高品质定制服务，为您提供个性化定制产品，包括T恤、卫衣、帽子等多种品类，专业工厂生产，品质保障'
  const defaultKeywords = 'Flashbase,定制,礼品,文创,印刷,T恤,卫衣,帽子,个性化定制'

  // 准备结构化数据
  let schemaScripts: any[] = []
  if (options.schema) {
    let jsonLd: string
    if (Array.isArray(options.schema)) {
      const data = options.schema.map((s) => ({
        '@context': 'https://schema.org',
        ...s,
      }))
      jsonLd = JSON.stringify(data, null, 2)
    } else {
      jsonLd = toJsonLd(options.schema)
    }

    schemaScripts = [
      {
        type: 'application/ld+json',
        innerHTML: jsonLd,
      },
    ]
  }

  const head = useHead({
    title: options.title || defaultTitle,
    meta: [
      { name: 'description', content: options.description || defaultDescription },
      { name: 'keywords', content: options.keywords || defaultKeywords },
      { property: 'og:title', content: options.ogTitle || options.title || defaultTitle },
      { property: 'og:description', content: options.ogDescription || options.description || defaultDescription },
      { property: 'og:type', content: options.ogType || 'website' },
      ...(options.ogImage ? [{ property: 'og:image', content: options.ogImage }] : []),
      ...(options.ogUrl ? [{ property: 'og:url', content: options.ogUrl }] : []),
    ],
    script: schemaScripts,
  })

  return head
}

export function updateSeo(options: SeoOptions) {
  return useSeo(options)
}

/**
 * 便捷函数：同时注入基础 Schema（WebSite + Organization）和 SEO
 */
export function useSeoWithBasicSchema(options: SeoOptions = {}, schemaOptions?: ISchemaOptions) {
  const website = createWebSiteSchema(schemaOptions?.website)
  const organization = createOrganizationSchema(schemaOptions?.organization)

  return useSeo({
    ...options,
    schema: [website, organization],
  })
}

/**
 * 便捷函数：注入产品 Schema 和 SEO
 */
export function useSeoWithProductSchema(options: SeoOptions = {}, productOptions?: any) {
  const product = createProductSchema(productOptions)

  return useSeo({
    ...options,
    schema: product,
  })
}
